import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Home, Shield, Target, CreditCard, ShoppingBag, Trophy, Lock, Users, Bell, CalendarDays, Sparkles } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { assetUrl } from "@/lib/store";
import { APP_ICON_URL } from "@/lib/assets";
import { userApi, auth, sessionProfile } from "@/lib/api";
import AvatarCropModal from "@/components/AvatarCropModal";

import AccountTab from '@/components/user/AccountTab';
import FamilyAiWorkspace from '@/components/ai/FamilyAiWorkspace';
import HomeTab from "@/components/user/HomeTab";
import MoneyTab from "@/components/user/MoneyTab";
import GoalsTab from "@/components/user/GoalsTab";
import GamificationTab from "@/components/user/GamificationTab";
import StoreCustomizationTab from "@/components/user/StoreCustomizationTab";
import SubscriptionTab from "@/components/user/SubscriptionTab";
import CalendarWorkspace from "@/components/user/CalendarWorkspace";
import ApprovalsUserTab from "@/components/user/ApprovalsUserTab";
import MobileBottomNav from "@/components/user/MobileBottomNav";
import NotificationCenter from "@/components/user/NotificationCenter";
import FinancialOperationModal from "@/components/user/FinancialOperationModal";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { Modal, PrimaryButton, GoldButton, SecondaryButton } from "@/components/ui/DesignSystem";
import { Clock as ClockIcon } from "lucide-react";
import { updateAppBadge } from "@/lib/pwa";

export default function UserDashboard({viewMode=false}) {
  const nav = useNavigate();
  const { iconMapping, refreshStore } = useTheme();
  const [me, setMe] = useState(null);
  const [txs, setTxs] = useState([]);
  const [userFeatures, setUserFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Active navigation tab
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return ['home', 'overview', 'money', 'bank_calendar', 'approvals', 'goals', 'gamification', 'store_customization', 'subscription', 'account', 'family_ai'].includes(requested) && !(viewMode && requested === 'family_ai') ? requested : 'home';
  });
  const [moneySubTab, setMoneySubTab] = useState("overview");
  const [homeOperation, setHomeOperation] = useState(null);

  // Modals & Drawers
  const [restrictedFeature, setRestrictedFeature] = useState(null);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const fileRef = useRef(null);
  const initialLoad = useRef(true);

  const fetchAll = useCallback(async () => {
    try {
      const cached = initialLoad.current && !viewMode ? sessionProfile(auth.getUser()) : null;
      initialLoad.current = false;
      const [m, t, f, n] = await Promise.all([
        cached ? Promise.resolve({ data: cached }) : userApi.get("/user/me"),
        userApi.get("/user/transactions").catch(() => ({ data: [] })),
        userApi.get("/user/features").catch(() => ({ data: { allowed_features: {} } })),
        userApi.get("/user/notifications").catch(() => ({ data: { items: [] } }))
      ]);
      setMe(m.data);
      setTxs(t.data);
      setUserFeatures(f.data?.allowed_features || {});
      setNotifications(n.data?.items || []);
      updateAppBadge((n.data?.items || []).filter(item => !item.is_read).length);
      refreshStore();
    } catch (error) {
      if (viewMode) { sessionStorage.removeItem('mb_view_token'); nav('/admin/dashboard/users'); return; }
      if (error?.response?.status === 428) { nav('/user/password-required'); return; }
      if ([401, 403, 404].includes(error?.response?.status)) {
        auth.forget('user', auth.getUser()); auth.clearUser(); nav('/users');
      } else toast.error('تعذر الاتصال. حسابك محفوظ؛ أعد المحاولة.');
    } finally {
      setLoading(false);
    }
  }, [nav, refreshStore, viewMode]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const refreshNotifications = () => fetchAll();
    window.addEventListener('mb:notification-received', refreshNotifications);
    return () => window.removeEventListener('mb:notification-received', refreshNotifications);
  }, [fetchAll]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('tab') === 'notifications') setNotificationsOpen(true);
  }, []);


  const leaveView = async () => { try { await (await import('@/lib/api')).adminApi.post(`/admin/view-as/${sessionStorage.getItem('mb_view_id')}/end`); } finally { sessionStorage.removeItem('mb_view_token'); sessionStorage.removeItem('mb_view_id'); nav('/admin/dashboard/users'); } };
  const logout = async () => {
    if(viewMode)return leaveView();
    try { await auth.logout('user'); nav('/'); }
    catch { toast.error('تعذر تسجيل الخروج. تحقق من الاتصال وأعد المحاولة.'); }
  };

  const switchAccountContext = async () => {
    try {
      if (me?.active_context === "MAIN_MANAGER" || String(me?.active_context || '').startsWith('ROLE:')) {
        await userApi.post("/user/roles/switch-context", { target_context: "PERSONAL" });
        auth.clearAdmin();
        await fetchAll();
        toast.success("تمت العودة إلى حسابك الشخصي");
        return;
      }
      const res = await userApi.get("/user/roles/my-roles").catch(() => null);
      if (res?.data?.is_manager_linked) {
        const switched = await userApi.post("/user/roles/switch-context", { target_context: "MAIN_MANAGER" });
        if (switched.data?.admin_token) auth.setAdmin(switched.data.admin_token);
        nav("/admin/dashboard/users");
        toast.success("تم الانتقال إلى وضع الإدارة الشاملة");
      } else if (res?.data?.assigned_roles?.length > 0) {
        const role = res.data.assigned_roles[0];
        const switched = await userApi.post("/user/roles/switch-context", { target_context: `ROLE:${role.role_id}` });
        if (switched.data?.admin_token) auth.setAdmin(switched.data.admin_token);
        nav('/admin/dashboard');
        toast.success(`تم الانتقال إلى وضع الإدارة (${role.name})`);
      } else {
        toast.info("لا يوجد حساب إداري مرتبط بهذا المستخدم");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "تعذر تبديل الحساب");
    }
  };

  const uploadAvatar = async blob => {
    const formData = new FormData();
    formData.append('file', blob, 'avatar.jpg');
    try {
      await userApi.post('/user/profile/avatar', formData);
      setCropFile(null);
      await fetchAll();
      toast.success('تم تحديث صورة الملف الشخصي');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'تعذر تحديث الصورة');
    }
  };

  const handleNavigate = (tab, subTab = null) => {
    if (tab === 'marketplace' || tab === 'customization') tab = 'store_customization';
    if (tab === 'simulator') tab = 'home';
    if (tab === 'external_money' || tab === 'bills') { setActiveTab('home'); return; }
    // Feature permission check
    if (tab === "store_customization" && userFeatures.customization === false) {
      setRestrictedFeature({ name: "تخصيص الواجهة والثيمات", plan: "bank" });
      return;
    }

    setActiveTab(desktopNavItems.some(item => item.id === tab) ? tab : 'home');
    if (subTab && tab === "money") {
      setMoneySubTab(subTab);
    }
  };

  const desktopNavItems = [
    { id: "account", label: "حسابي", icon: Lock, iconKey: "settings" },
    { id: "home", label: "الرئيسية", icon: Home, iconKey: "home" },
    { id: "money", label: "الأموال والعمليات", icon: Shield, iconKey: "money" },
    { id: "bank_calendar", label: "تقويم البنك", icon: CalendarDays, iconKey: "calendar" },
    { id: "approvals", label: "طلبات الموافقات", icon: ClockIcon, iconKey: "notifications" },
    { id: "goals", label: "الأهداف والصناديق", icon: Target, iconKey: "goals" },
    ...(!viewMode ? [{ id: 'family_ai', label: 'بنك العائلة AI', icon: Sparkles }] : []),
    { id: "gamification", label: "المكافآت والجوائز", icon: Trophy },
    { id: "store_customization", label: "المتجر والتخصيص", icon: ShoppingBag, iconKey: "store" },
    { id: "subscription", label: "الاشتراك والخطة", icon: CreditCard }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--c-cream)] flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-[var(--c-navy)]/20 border-t-[var(--c-navy)] rounded-full animate-spin" />
        <p className="text-xs font-bold text-[var(--c-navy)]">جاري تحميل بيانات حساب بنك العائلة...</p>
      </div>
    );
  }

  if (!me) return <main dir="rtl" className="min-h-screen grid place-items-center"><div>تعذر تحميل الحساب. حسابك محفوظ.<button onClick={fetchAll}>إعادة المحاولة</button></div></main>;

  return (
    <div className="family-auth-shell family-user-dashboard-shell min-h-screen text-[var(--c-navy)] flex flex-col font-sans pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-6">
      {/* 1. TOP GLOBAL NAVBAR */}
      {viewMode&&<div className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b border-amber-300 bg-[var(--color-surface)] p-3 text-sm"><b>تعمل كمدير بالنيابة عن: {me.username}</b><button className="btn-navy" onClick={leaveView}>العودة إلى لوحة المدير</button></div>}
      <header className="family-user-header sticky top-0 z-30">
        <div className="family-user-header__side">
          <button disabled={viewMode} onClick={switchAccountContext} className="fb-header-control" title="التبديل إلى لوحة الإدارة المرتبطة" aria-label="التبديل إلى لوحة الإدارة المرتبطة"><Shield className="h-5 w-5" /><span>{me?.active_context === 'MAIN_MANAGER' || String(me?.active_context || '').startsWith('ROLE:') ? 'الحساب الشخصي' : 'لوحة الإدارة'}</span></button>
        </div>
        <button type="button" className="family-user-brand" onClick={() => handleNavigate('home')}>
          <img src={APP_ICON_URL} alt="أيقونة بنك العائلة الرسمية" />
          <span><b>بنك العائلة</b><small>ننمو معًا.</small></span>
        </button>
        <div className="family-user-header__side family-user-header__side--end">
          <button
            onClick={() => handleNavigate("subscription")}
            className="fb-header-control fb-header-plan"
          >
            <CreditCard className="w-3.5 h-3.5" />
            <span>{me?.subscription?.type === 'bank' ? 'اشتراك بنك العائلة' : me?.subscription?.type === 'bank_amwali_plus' ? 'باقة أموالي +' : 'بدون اشتراك'}</span>
          </button>
          <button onClick={() => setNotificationsOpen(true)} className="fb-header-control relative" title="الإشعارات" aria-label="الإشعارات">
            <Bell className="w-5 h-5" />
            {notifications.some(n => !n.is_read) && <span className="absolute -top-1 -end-1 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">{notifications.filter(n => !n.is_read).length}</span>}
          </button>
          <button disabled={viewMode} onClick={() => nav('/users?switch=1')} className="fb-header-control" title="تبديل الحساب" aria-label="تبديل الحساب"><Users className="w-5 h-5" /></button>
          <button onClick={logout} className="fb-header-control fb-header-control--danger" title="تسجيل الخروج" aria-label="تسجيل الخروج"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>

      {/* 2. MAIN BODY GRID (DESKTOP SIDEBAR + CONTENT AREA) */}
      <div className={`flex-1 w-full mx-auto p-3 sm:p-5 lg:p-6 gap-5 lg:gap-6 ${['external_money','family_ai'].includes(activeTab) ? "max-w-[1440px] block" : `max-w-[1440px] grid md:grid-cols-[minmax(13rem,15rem)_minmax(0,1fr)]`}`}>
        {/* DESKTOP / TABLET SIDEBAR */}
        {!['external_money','family_ai'].includes(activeTab) && <aside className={`mb-premium-surface hidden md:block border rounded-3xl p-3 shadow-[var(--card-shadow)] h-fit space-y-1.5 sticky top-20 max-h-[calc(100dvh-6rem)] overflow-y-auto`}>
          <div className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider px-3 py-2">
            القائمة الرئيسية
          </div>

          {desktopNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id || (activeTab === "overview" && item.id === "home");
            return (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                aria-current={isActive ? 'page' : undefined}
                className={`family-nav-item w-full flex items-center justify-between px-3.5 py-2.5 rounded-2xl text-xs font-bold transition-all ${
                  isActive
                    ? "bg-[image:var(--gradient-electric)] text-white shadow-[var(--glow-blue)]"
                    : "text-[var(--color-text-primary)] hover:bg-[var(--color-surface-secondary)]"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <SemanticIcon mapping={iconMapping} iconKey={item.iconKey} fallback={Icon} className={`w-4 h-4 ${isActive ? "text-[var(--c-gold)]" : "text-[var(--c-muted)]"}`} />
                  <span>{item.label}</span>
                </div>
              </button>
            );
          })}
        </aside>}

        {/* MAIN CONTENT CANVAS */}
        <main className="min-w-0">
          <fieldset className="min-w-0" ><ErrorBoundary key={activeTab}>
            {activeTab === 'family_ai' && !viewMode && <FamilyAiWorkspace />}
            {activeTab === "account" && <AccountTab readOnly={viewMode} />}
            {(activeTab === "home" || activeTab === "overview") && (
              <HomeTab
                me={me}
                txs={txs}
                onNavigate={handleNavigate}
                onOpenCustomizer={() => setCustomizerOpen(true)}
                onStartOperation={setHomeOperation}
                onEditAvatar={() => fileRef.current?.click()}
              />
            )}

            {activeTab === "money" && (
              <MoneyTab me={me} onUpdate={fetchAll} initialSubTab={moneySubTab} />
            )}

            {activeTab === "bank_calendar" && (
              <CalendarWorkspace realm="bank" />
            )}


            {activeTab === "approvals" && (
              <ApprovalsUserTab me={me} onUpdate={fetchAll} />
            )}

            {activeTab === "goals" && (
              <GoalsTab me={me} onUpdate={fetchAll} />
            )}



            {activeTab === "gamification" && (
              <GamificationTab me={me} onUpdate={fetchAll} />
            )}

            {activeTab === "store_customization" && <StoreCustomizationTab me={me} onUpdate={fetchAll} />}

            {activeTab === "subscription" && (
              <SubscriptionTab me={me} onUpdate={fetchAll} />
            )}


          </ErrorBoundary></fieldset>
        </main>
      </div>

      {/* 3. MOBILE BOTTOM NAVIGATION */}
      {!['external_money','family_ai'].includes(activeTab) && <MobileBottomNav
        activeTab={activeTab === "overview" ? "home" : activeTab}
        onTabChange={(tab) => handleNavigate(tab)}
        onMore={() => setMobileMenuOpen(true)}
      />}

      {mobileMenuOpen && <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="المزيد من الأقسام">
        <button className="absolute inset-0 bg-slate-950/50" onClick={() => setMobileMenuOpen(false)} aria-label="إغلاق القائمة" />
        <section className="absolute inset-x-0 bottom-0 max-h-[78dvh] overflow-y-auto rounded-t-3xl bg-[var(--color-surface)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl fade-up">
          <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[var(--color-border)]" />
          <div className="flex items-center justify-between mb-4"><div><h2 className="font-black text-lg">المزيد</h2><p className="text-xs text-[var(--color-text-secondary)] mt-1">كل أدوات حسابك في مكان واحد</p></div><button className="w-10 h-10 rounded-xl border border-[var(--color-border)]" onClick={() => setMobileMenuOpen(false)}>×</button></div>
          <div className="grid grid-cols-2 gap-2">{desktopNavItems.filter(item => !['home','money','goals'].includes(item.id)).map(item => { const Icon=item.icon; return <button key={item.id} onClick={() => { setMobileMenuOpen(false); handleNavigate(item.id); }} className="min-h-20 min-w-0 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-secondary)]/40 p-3 text-start flex flex-col justify-between last:col-span-2 last:min-h-16">{item.logo?<img src={item.logo} alt="أموالي +" className="h-8 w-8 rounded-lg object-contain"/>:<SemanticIcon mapping={iconMapping} iconKey={item.iconKey} fallback={Icon} className="w-5 h-5 text-[var(--c-gold)]"/>}<span className="text-xs font-bold leading-5">{item.label}</span></button>; })}</div>
        </section>
      </div>}

      <FinancialOperationModal operation={homeOperation} me={me} onClose={() => setHomeOperation(null)} onUpdate={fetchAll} />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={event => { const file = event.target.files?.[0]; if (file) setCropFile(file); event.target.value = ''; }} data-testid="avatar-input" />
      {cropFile && <AvatarCropModal file={cropFile} onCancel={() => setCropFile(null)} onConfirm={uploadAvatar} />}

      {/* 4. FEATURE RESTRICTION MODAL */}
      <Modal isOpen={notificationsOpen} onClose={() => setNotificationsOpen(false)} title="الإشعارات" maxWidth="max-w-2xl">
        <NotificationCenter notifications={notifications} setNotifications={setNotifications} onNavigate={(tab) => { setNotificationsOpen(false); handleNavigate(tab); }} />
      </Modal>

      {/* 4. FEATURE RESTRICTION MODAL */}
      <Modal
        isOpen={Boolean(restrictedFeature)}
        onClose={() => setRestrictedFeature(null)}
        title="ميزة مقفلة"
        maxWidth="max-w-sm"
      >
        <div className="text-center space-y-4 py-2">
          <div className="w-14 h-14 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7" />
          </div>
          <h4 className="text-base font-black text-[var(--c-navy)]">{restrictedFeature?.name}</h4>
          <p className="text-xs text-[var(--c-muted)] leading-relaxed">
            {restrictedFeature?.plan === 'bank_amwali_plus' ? 'أموالي + متاحة فقط مع باقة أموالي + الفعالة.' : 'هذه الميزة تحتاج اشتراكاً فعالاً في بنك العائلة.'}
          </p>
          <div className="flex gap-3 pt-2">
            <GoldButton
              onClick={() => {
                setRestrictedFeature(null);
                handleNavigate("subscription");
              }}
              fullWidth
              size="sm"
            >
              عرض الاشتراكات
            </GoldButton>
            <SecondaryButton onClick={() => setRestrictedFeature(null)} size="sm">
              إلغاء
            </SecondaryButton>
          </div>
        </div>
      </Modal>

      {/* 5. DASHBOARD CUSTOMIZER DRAWER */}
      <Modal
        isOpen={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
        title="تخصيص شاشة الواجهة الرئيسية"
      >
        <div className="space-y-4 text-xs font-medium text-slate-700">
          <p className="text-[var(--c-muted)]">يمكنك ترتيب وإظهار عناصر الشاشة الرئيسية حسب تفضيلاتك الشخصية:</p>
          <div className="space-y-2">
            {[
              { id: "balance", label: "بطاقة الرصيد الرئيسي" },
              { id: "quick_actions", label: "أزرار الإجراءات السريعة" },
              { id: "transactions", label: "معاينة المعاملات الأخيرة" },
              { id: "gamification", label: "مكافآت ومستوى الحساب" }
            ].map(w => (
              <div key={w.id} className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                <span>{w.label}</span>
                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">مُفعّل</span>
              </div>
            ))}
          </div>
          <PrimaryButton onClick={() => { setCustomizerOpen(false); toast.success("تم حفظ ترتيب الواجهة"); }} fullWidth>
            حفظ الترتيب
          </PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}

function SemanticIcon({mapping,iconKey,fallback:Fallback,className}){
  const src=assetUrl(iconKey&&mapping?.[iconKey]);
  return src?<img src={src} alt="" className={`${className} object-contain`}/>:<Fallback className={className}/>;
}
